






















































































































































































import Vue from "vue";
import { computed, ref } from "@vue/composition-api";
import Icon from "@/components/Icon.vue";
import GenAiCard from "./GenAiCard.vue";
import GenAiDonut from "./GenAiDonut.vue";
import LevelSlider from "./LevelSlider.vue";
import ViewModeToggle from "./ViewModeToggle.vue";
import SkeletonLoaderItem from "@/components/SkeletonLoaderItem.vue";
import { LOAD_FAILED_TEXT, state } from "../data/state";
import { use_visibility_stop } from "../data/stops";
import {
  AiFileActivityId,
  INFO_CATEGORIES,
  InfoCategory,
  SENSITIVITY_STOPS,
  activities_for,
} from "../data/model";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";
import { show_gen_ai_events } from "../data/drilldown";
import { visibility_share_line } from "../data/share";
import { use_view_mode } from "../data/view-mode";

type Tone = "top" | "bottom" | "plain";

interface MatrixRow {
  id: AiFileActivityId;
  name: string;
  /** one per category, in INFO_CATEGORIES order, at the current ruler stop */
  values: number[];
  tones: Tone[];
  total: number;
}

/** the sensitivity ruler's active stops as the numeric levels rows carry */
const SENS_BY_TONE: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

/**
 * Marks each row's highest cell and its lowest, first occurrence of each, as
 * gen-ai-ui does. A row of zeros marks nothing: a yellow "highest" chip on a
 * 0 would point at a peak that is not there.
 */
const row_tones = (values: number[]): Tone[] => {
  const max = Math.max(...values);
  const min = Math.min(...values);
  let top_taken = false;
  let bottom_taken = false;
  return values.map((v) => {
    if (!top_taken && max > 0 && v === max) {
      top_taken = true;
      return "top";
    }
    if (!bottom_taken && min !== max && v === min) {
      bottom_taken = true;
      return "bottom";
    }
    return "plain";
  });
};

/**
 * Widget 4 — "AI Activities" (gen-ai-ui, Sep 2026).
 *
 * The AI-originated activities (generated, modified, read) against the six
 * information categories, from the sensitivity cube. The employee -> AI side
 * is "Information Shared With AI" next to it.
 */
export default Vue.extend({
  components: {
    Icon,
    GenAiCard,
    GenAiDonut,
    LevelSlider,
    ViewModeToggle,
    SkeletonLoaderItem,
  },
  setup() {
    const palette = useGenAiPalette();
    const view = use_view_mode("activityMatrix");
    const is_loading = computed(() => state.loading.value.is_loading);
    const categories = INFO_CATEGORIES;

    /** opens on "All", so the card shows the full picture first */
    const level = ref(SENSITIVITY_STOPS.length);
    const active_keys = computed(() => SENSITIVITY_STOPS.slice(0, level.value));

    // each stop is its own query: one count per category and activity
    const at = use_visibility_stop(level);

    const rows = computed<MatrixRow[]>(() => {
      const counts = at.data.value;
      return activities_for("ai").map(({ id, name }) => {
        const values = categories.map((c) => (counts ? counts[c.id][id] : 0));
        return {
          id,
          name,
          values,
          tones: row_tones(values),
          total: values.reduce((a, b) => a + b, 0),
        };
      });
    });

    const total = computed(() =>
      rows.value.reduce((sum, row) => sum + row.total, 0)
    );

    /** a failed request has no count: a dash, never a zero */
    const load_failed = computed(() => state.failed.value.labels || at.failed.value);
    const stop_loading = computed(() => !is_loading.value && at.loading.value);

    const subtitle = computed(() =>
      visibility_share_line(state.overview.value.shares, "ai", total.value)
    );

    // sized in the dashboard unit, not rem, so the columns keep their
    // relationship to the numbers sitting in them at every width
    const grid_cols =
      `minmax(${SIZE.matrixLabelCol},1.2fr) ` +
      `repeat(${categories.length},minmax(${SIZE.matrixCell},1fr))`;

    /** soft chips on the row's extremes, plain text everywhere else */
    const cell_style = (tone: Tone) => {
      if (tone === "plain") {
        return { color: palette.value.text.label };
      }
      const t = palette.value.matrixTone[tone];
      return { background: t.bg, color: t.text, fontWeight: 600 };
    };

    const tone_hint = (tone: Tone) =>
      tone === "top" ? "Highest" : tone === "bottom" ? "Lowest" : "Typical";

    const aria_for = (row: MatrixRow, col: InfoCategory, i: number) =>
      `Open ${row.values[i]} ${row.name} activities on ${col.name} information` +
      (row.tones[i] === "plain" ? "" : ` — ${tone_hint(row.tones[i]).toLowerCase()} in this row`);

    const sensitivities = () =>
      active_keys.value.map((k) => SENS_BY_TONE[k]);

    const show_cell = (row: MatrixRow, col: InfoCategory) =>
      show_gen_ai_events(
        col.name + ": <b>" + row.name + "</b>",
        {
          widget: "matrix",
          relation: row.id,
          direction: "ai",
          category: col.id,
          sensitivities: sensitivities(),
        },
        // a log of what the AI did, not a set of files
        { ai_activities: true }
      );

    const show_activity = (row?: MatrixRow) =>
      row &&
      show_gen_ai_events(
        "<b>" + row.name + "</b>: AI Activities",
        {
          widget: "matrix",
          relation: row.id,
          direction: "ai",
          sensitivities: sensitivities(),
        },
        { ai_activities: true }
      );

    const donut_items = computed(() =>
      rows.value.map((row) => ({
        label: row.name,
        value: row.total,
        color: palette.value.activityChart[row.id],
      }))
    );

    return {
      load_failed,
      stop_loading,
      LOAD_FAILED_TEXT,
      view,
      is_loading,
      categories,
      level,
      rows,
      total,
      subtitle,
      grid_cols,
      cell_style,
      tone_hint,
      aria_for,
      show_cell,
      show_activity,
      donut_items,
      legend: [
        { key: "top", label: "Highest" },
        { key: "bottom", label: "Lowest" },
      ],
      // gen-ai-ui SensitivityRuler: classifications, not risk levels
      stops: [
        { label: "High", pill: "High Sensitivity", key: "high" },
        { label: "+ Medium", pill: "High + Medium", key: "medium" },
        { label: "+ Low", pill: "High + Med + Low", key: "low" },
        { label: "All", pill: "Any Content", key: "none" },
      ],
      T: palette,
      TY: TYPE,
      S: SIZE,
    };
  },
});
